import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import Text from "../components/Text";
import { rgLevelPrice, rgLabel, RG_DEFAULT_LEVELS } from "../calculoRGMath";

// Wrapper do Cálculo R/G. Âncoras: (x1,y1) = entrada (nível 0), (x2,y2) = stop
// (nível -1). Paridade Silverlight (CalculoRG.cs):
//   - cada linha é um raio horizontal que NASCE no X do stop (pfStart = p2.X) e
//     se estende à direita (pfEnd = borda do canvas) → StraightLine type RAY
//     com x2Value = x+1 para fixar a direção (o gesto natural é vertical,
//     x1 === x2, que degeneraria um RAY pelas âncoras);
//   - pen POR LINHA com fallback na pen do objeto (Split[i].LinePen ?? base);
//   - rótulo abaixo da linha, na cor da linha quando TextBrush null;
//   - linha-guia entrada→stop visível durante interação (InMove no legado).
class EachCalculoRG extends Component {
  constructor(props) {
    super(props);

    this.handleAnchor1Drag = this.handleAnchor1Drag.bind(this);
    this.handleAnchor2Drag = this.handleAnchor2Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }
  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }
  handleLineDragStart() {
    const { x1, y1, x2, y2 } = this.props;

    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
    };
  }
  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }
  handleAnchor1Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x2, y2 } = this.props;

    const [x1, y1] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  handleAnchor2Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, y1 } = this.props;

    const [x2, y2] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  render() {
    const { x1, x2, y1, y2 } = this.props;
    const { interactive, yDisplayFormat, appearance } = this.props;
    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;

    const stroke = appearance.stroke?.color || "#FFFFFF";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash || "Solid";

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 10;
    const fontColor = appearance.font?.color || stroke;
    const labelUsaCorDaLinha = appearance.labelUsaCorDaLinha !== false;

    const circleRadius = appearance.r || 6;

    const configuredLevels = appearance.levels || RG_DEFAULT_LEVELS;
    const activeLevels = configuredLevels.filter((l) => l.display !== false);

    // Raio nasce no X do stop (2º ponto), como no legado (pfStart = p2.X)
    const lineStartX = x2;
    const lines = activeLevels.map((lvl) => ({
      level: lvl.level,
      color: lvl.color || stroke,
      y: rgLevelPrice({ entrada: y1, stop: y2, level: lvl.level }),
    }));

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const hoverHandler = interactive
      ? { onHover: this.handleHover, onUnHover: this.handleHover }
      : {};

    const showControls = selected || hover;

    return (
      <g>
        {lines.map((line, j) => {
          const text = rgLabel({
            price: line.y,
            entrada: y1,
            yDisplayFormat,
          });

          const xyProvider = ({ xScale, chartConfig }) => {
            const { yScale } = chartConfig;
            const x = xScale(lineStartX) + 10;
            // Rótulo ABAIXO da linha (legado: rect do texto começa no Y da linha)
            const y = yScale(line.y) + fontSize + 2;
            return [x, y];
          };

          return (
            <g key={j}>
              <StraightLine
                ref={this.saveNodeType(`line_${j}`)}
                selected={selected || hover}
                {...hoverHandler}
                type="RAY"
                x1Value={lineStartX}
                y1Value={line.y}
                x2Value={lineStartX + 1}
                y2Value={line.y}
                stroke={line.color}
                strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
                strokeOpacity={strokeOpacity}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass="react-stockcharts-move-cursor"
                onDragStart={this.handleLineDragStart}
                onDrag={this.handleLineMove}
                onDragComplete={onDragComplete}
              />
              <Text
                selected={selected}
                xyProvider={xyProvider}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fill={labelUsaCorDaLinha ? line.color : fontColor}
              >
                {text}
              </Text>
            </g>
          );
        })}

        {/* Linha-guia entrada→stop. No Silverlight só aparecia durante o move;
            no odin (decisão do Guto) fica SEMPRE visível, branco tracejado. */}
        <StraightLine
          selected={selected}
          type="LINE"
          x1Value={x1}
          y1Value={y1}
          x2Value={x2}
          y2Value={y2}
          stroke="#FFFFFF"
          strokeWidth={1}
          strokeOpacity={1}
          strokeDasharray="Dash"
        />

        {/* Handles nas duas âncoras (entrada e stop), como os ControlPoints */}
        <ClickableCircle
          ref={this.saveNodeType("anchor1")}
          show={showControls}
          cx={x1}
          cy={y1}
          r={circleRadius}
          fill="#FFFFFF"
          stroke="#000000"
          strokeWidth={2}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDrag={this.handleAnchor1Drag}
          onDragComplete={onDragComplete}
        />
        <ClickableCircle
          ref={this.saveNodeType("anchor2")}
          show={showControls}
          cx={x2}
          cy={y2}
          r={circleRadius}
          fill="#FFFFFF"
          stroke="#000000"
          strokeWidth={2}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDrag={this.handleAnchor2Drag}
          onDragComplete={onDragComplete}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

EachCalculoRG.propTypes = {
  x1: PropTypes.any.isRequired,
  x2: PropTypes.any.isRequired,
  y1: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,

  yDisplayFormat: PropTypes.func,
  type: PropTypes.string,
  selected: PropTypes.bool.isRequired,

  appearance: PropTypes.shape({
    levels: PropTypes.array,
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    labelUsaCorDaLinha: PropTypes.bool,
    r: PropTypes.number,
  }).isRequired,

  interactive: PropTypes.bool.isRequired,
  hoverText: PropTypes.object.isRequired,

  index: PropTypes.number,
  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachCalculoRG.defaultProps = {
  yDisplayFormat: (d) => d.toFixed(2),
  interactive: true,
  selected: false,

  appearance: {
    stroke: { color: "#FFFFFF", width: 1, opacity: 1, dash: "Solid" },
    levels: RG_DEFAULT_LEVELS,
    font: {
      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
      fontSize: 10,
      color: "#FFFFFF",
    },
    labelUsaCorDaLinha: true,
    r: 6,
  },

  onDrag: noop,
  onDragComplete: noop,

  hoverText: {
    enable: false,
  },
};

export default EachCalculoRG;
